import angular, { Injectable, IPromise } from "angular";
import { RecordDataSource } from "../../grids";
import { ColDef } from "../../grids/columns/colDef";
import { IGridSettings } from "../../grids/settings/gridSettingsService";
import { cloneDeep, lowerCase } from "lodash";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../grids/columns/columnDefinitionSetService";

export interface GridConfigurationFactorySet {
  [name: string]: Injectable<GridConfigurationFactory>;
}

export interface GridConfiguration {
  columns?: ColDef[];
  columnDefinitionSet?: string;
  onItemCommand?: Injectable<(...args: any[]) => IPromise<void>>;
  gridDataSource?: RecordDataSource;
  defaultSort?: string;
  nameSingular?: string;
  namePlural?: string;
  showAddButton?: boolean;
  showAddButtonPermission?: string | string[];
  gridSelectionChanged?: Injectable<(...args: any[]) => void>;
  showSelectButtons?: boolean;
  gridSettings?: IGridSettings;
  initialSelectedIds?: number[];
  onAddNew?: () => IPromise<void>;
}

export const vsServiceName = "recordGridViewService";
export const vsProviderName = `${vsServiceName}Provider`;

export interface GridConfigurationFactory {
  (...args: any[]): GridConfiguration;
}

export class RecordGridViewService {
  constructor(
    private readonly $inject: angular.auto.IInjectorService,
    private readonly configs: GridConfigurationFactorySet,
    private readonly columnDefinitionSetService: ColumnDefinitionSetService
  ) {}

  getGridConfiguration(name: string): GridConfiguration | undefined {
    const gridConfigurationFactory =
      this.configs[RecordGridViewService.normalizeName(name)];
    if (gridConfigurationFactory === undefined) return undefined;

    var configs = RecordGridViewService.getGridConfigurations(
      this.$inject,
      gridConfigurationFactory,
      this.columnDefinitionSetService
    );

    return configs;
  }

  private static getGridConfigurations(
    $injector: angular.auto.IInjectorService,
    config: Injectable<GridConfigurationFactory>,
    columnDefinitionSetService: ColumnDefinitionSetService
  ): GridConfiguration {
    const gridConfig = $injector.invoke(config);
    if (gridConfig.columnDefinitionSet && !gridConfig.columns) {
      gridConfig.columns = columnDefinitionSetService.getColumnDefinitionSet(
        gridConfig.columnDefinitionSet
      );
    }

    gridConfig.columns = cloneDeep(gridConfig.columns);

    return gridConfig;
  }

  static normalizeName(name: string): string {
    return lowerCase(name);
  }
}

export class RecordGridViewServiceProvider implements angular.IServiceProvider {
  private readonly _configs: GridConfigurationFactorySet = {};

  addGridConfiguration(
    name: string,
    config: Injectable<GridConfigurationFactory>
  ) {
    this._configs[RecordGridViewService.normalizeName(name)] = config;
  }

  $get: any = [
    "$injector",
    ColumnDefinitionSetServiceName,
    (
      $injector: angular.auto.IInjectorService,
      columnDefinitionSetService: ColumnDefinitionSetService
    ) => {
      return new RecordGridViewService(
        $injector,
        this._configs,
        columnDefinitionSetService
      );
    },
  ];
}
